/**
 * CoachFeedbackReviewModal (MED-330)
 * CM-facing modal showing all coaches' feedback submissions for a midpoint/endpoint survey.
 * Read-only view with coach name, submission date, satisfaction rating, and all responses.
 */

import { useState, useEffect } from 'react';
import { Modal, Text, Stack, Group, Badge, Divider, Loader, Rating, Paper } from '@mantine/core';
import {
  feedbackSurveysApi,
  type CoachFeedbackResponse,
  type CoachFeedbackSubmission,
  type FeedbackSurveyQuestion,
} from '../../../../services/api/feedbackSurveys';
import { formatDate } from '../../../../utils/formatters';

interface CoachFeedbackReviewModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  surveyType: 'midpoint' | 'endpoint';
}

export function CoachFeedbackReviewModal({
  opened,
  onClose,
  cohortId,
  surveyType,
}: CoachFeedbackReviewModalProps) {
  const [data, setData] = useState<CoachFeedbackResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setLoading(true);
    feedbackSurveysApi
      .getCoachResponses(cohortId, surveyType)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [opened, cohortId, surveyType]);

  const surveyLabel = surveyType === 'midpoint' ? 'Midpoint' : 'Endpoint';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Text fw={600} size="lg">
            Coach {surveyLabel} Feedback
          </Text>
          {data?.submissions && (
            <Badge size="sm" color="violet" variant="light">
              {data.submissions.length} response{data.submissions.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </Group>
      }
      size="lg"
      styles={{
        body: { padding: '0 24px 24px' },
      }}
    >
      {loading ? (
        <Stack align="center" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading coach feedback...
          </Text>
        </Stack>
      ) : !data?.submissions?.length ? (
        <Stack align="center" py="xl">
          <Text size="sm" c="dimmed">
            No coach feedback submitted yet
          </Text>
        </Stack>
      ) : (
        <Stack gap="lg">
          {data.submissions.map((submission) => (
            <CoachSubmissionCard
              key={submission.id}
              submission={submission}
              questions={data.questions}
            />
          ))}
        </Stack>
      )}
    </Modal>
  );
}

function CoachSubmissionCard({
  submission,
  questions,
}: {
  submission: CoachFeedbackSubmission;
  questions: FeedbackSurveyQuestion[];
}) {
  const responses = (submission.parsed_data?.responses || {}) as Record<string, unknown>;

  // Find satisfaction rating (first scale question)
  const scaleQuestion = questions.find((q) => q.question_type === 'scale');
  const satisfactionValue = scaleQuestion ? Number(responses[scaleQuestion.id]) || null : null;

  return (
    <Paper withBorder p="md" radius="sm">
      <Stack gap="sm">
        {/* Coach header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Text fw={600} size="sm">
              {submission.coach_name}
            </Text>
            {satisfactionValue !== null && (
              <Group gap={4}>
                <Rating value={satisfactionValue} readOnly size="xs" count={5} />
                <Text size="xs" c="dimmed">
                  ({satisfactionValue}/5)
                </Text>
              </Group>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {formatDate(submission.submitted_at)}
          </Text>
        </Group>

        <Divider />

        {/* Responses */}
        <Stack gap="md">
          {questions.map((question) => {
            const answer = responses[question.id];
            if (answer === undefined || answer === null || answer === '') return null;

            return (
              <Stack key={question.id} gap={4}>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                  {question.question_text}
                </Text>
                {question.question_type === 'scale' ? (
                  <Group gap={4}>
                    <Rating value={Number(answer)} readOnly size="sm" count={question.scale_max || 5} />
                    <Text size="sm">({String(answer)}/{question.scale_max || 5})</Text>
                  </Group>
                ) : (
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {String(answer)}
                  </Text>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default CoachFeedbackReviewModal;
